import React, { useEffect, useState } from "react";
import { GetAreaPropertyCount, AreaPropertyCountUpdate, AddAreaPropertyCount } from "service/api";
import {
    Card,
    Col,
    Container,
    Row,
    CardBody,
    Button,
    Input
} from "reactstrap";
import { toast } from "react-toastify";
import AdminGuard from "helpers/AuthType/AdminGuard";

const AddSummary = () => {
    const [areaPropertyCounts, setAreaPropertyCounts] = useState([]);
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [newArea, setNewArea] = useState({
        area: "",
        buy_com: "",
        buy_res: "",
        rent_com: "",
        rent_res: "",
        lease_com: "",
        lease_res: ""
    });
    const resultsPerPage = 10;

    const fetchPropertyCount = async () => {
        try {
            const response = await GetAreaPropertyCount();
            if (response?.properties_count) {
                setAreaPropertyCounts(response.properties_count);
            } else {
                setError("Property count data not found.");
            }
        } catch (err) {
            setError("Error fetching property count data.");
            console.error(err);
        }
    };

    useEffect(() => {
        fetchPropertyCount();
    }, []);

    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => setSuccessMessage(""), 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    const handleAddNew = () => {
        setIsAddingNew(true);
        setNewArea({
            area: "",
            buy_com: "",
            buy_res: "",
            rent_com: "",
            rent_res: "",
            lease_com: "",
            lease_res: ""
        });
    };

    const handleCancelAdd = () => {
        setIsAddingNew(false);
    };

    const handleNewAreaChange = (e) => {
        const { name, value } = e.target;
        setNewArea(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmitNewArea = async () => {
        if (!newArea.area.trim()) {
            toast.error("Area name is required");
            return;
        }

        const loadingToast = toast.loading("Adding new area...");

        try {
            const result = await AddAreaPropertyCount(newArea);

            if (result?.data?.success) {  // Check for success in the response data
                toast.update(loadingToast, {
                    render: `${result.data.data.area} added successfully!`,  // Use data from response
                    type: "success",
                    isLoading: false,
                    autoClose: 3000,
                    closeOnClick: true,
                    pauseOnHover: true  // Fixed typo: was pauseOnHover
                });
                setIsAddingNew(false);
                await fetchPropertyCount();
            } else {
                toast.update(loadingToast, {
                    render: result?.data?.message || "Failed to add new area",
                    type: "error",
                    isLoading: false,
                    autoClose: 3000,
                    closeOnClick: true,
                    pauseOnHover: true
                });
            }
        } catch (error) {
            toast.update(loadingToast, {
                render: error.response?.data?.message || "Error adding new area",
                type: "error",
                isLoading: false,
                autoClose: 3000,
                closeOnClick: true,
                pauseOnHover: true
            });
            console.error("Add area error:", error);
        }
    };

    const filteredData = areaPropertyCounts.filter(item =>
        item.area?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalResults = filteredData.length;
    const totalPages = Math.ceil(totalResults / resultsPerPage);
    const startIndex = (currentPage - 1) * resultsPerPage;
    const paginatedData = filteredData.slice(startIndex, startIndex + resultsPerPage);

    const goToPage = (page) => {
        if (page < 1) page = 1;
        else if (page > totalPages) page = totalPages;
        setCurrentPage(page);
    };

    return (
        <AdminGuard>
            <Container fluid style={{ padding: "2rem 1rem" }}>
                <Row className="justify-content-center">
                    <Col xs={12}>
                        <Card
                            style={{
                                background: "linear-gradient(to right, #1f2937, #111827)",
                                borderRadius: "12px",
                                boxShadow: "0 10px 30px rgba(0, 0, 0, 0.4)",
                                color: "white",
                                marginTop: "10%"
                            }}
                        >
                            <CardBody style={{ padding: "2rem" }}>
                                <h3
                                    style={{
                                        textAlign: "center",
                                        marginBottom: "1.5rem",
                                        fontWeight: "600"
                                    }}
                                >
                                    Vadodara Property Market - Update Detail
                                </h3>

                                {error && (
                                    <p style={{ color: "red", textAlign: "center" }}>{error}</p>
                                )}

                                {successMessage && (
                                    <p style={{ color: "lightgreen", textAlign: "center" }}>
                                        {successMessage}
                                    </p>
                                )}

                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "1.5rem" }}>
                                    <div style={{ width: "70%" }}>
                                        <Input
                                            type="text"
                                            placeholder="Search by Area..."
                                            value={searchTerm}
                                            onChange={e => {
                                                setSearchTerm(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                            style={{
                                                padding: "0.75rem 1rem",
                                                borderRadius: "50px",
                                                border: "1px solid #6b7280",
                                                backgroundColor: "#111827",
                                                color: "white",
                                                fontSize: "1rem",
                                                boxShadow: "inset 0 0 5px rgba(0,0,0,0.3)"
                                            }}
                                        />
                                    </div>
                                    <Button
                                        color="primary"
                                        onClick={handleAddNew}
                                        style={{
                                            borderRadius: "50px",
                                            fontWeight: "600",
                                            padding: "0.5rem 1.5rem"
                                        }}
                                        disabled={isAddingNew}
                                    >
                                        Add New Area
                                    </Button>
                                </div>

                                <div style={{ overflowX: "auto" }}>
                                    <table
                                        style={{
                                            width: "100%",
                                            minWidth: "800px",
                                            borderCollapse: "collapse"
                                        }}
                                    >
                                        <thead>
                                            <tr style={{ backgroundColor: "#4b5563" }}>
                                                <th rowSpan="2" style={headerStyle}>
                                                    Area
                                                </th>
                                                <th colSpan="2" style={headerStyle}>
                                                    Buy
                                                </th>
                                                <th colSpan="2" style={headerStyle}>
                                                    Rent
                                                </th>
                                                <th colSpan="2" style={headerStyle}>
                                                    Lease
                                                </th>
                                                <th colSpan="2" style={headerStyle}>
                                                    Owners
                                                </th>
                                                <th rowSpan="2" style={headerStyle}>
                                                    Actions
                                                </th>
                                            </tr>
                                            <tr style={{ backgroundColor: "#374151" }}>
                                                <th style={headerStyle}>Commercial</th>
                                                <th style={headerStyle}>Residential</th>
                                                <th style={headerStyle}>Commercial</th>
                                                <th style={headerStyle}>Residential</th>
                                                <th style={headerStyle}>Commercial</th>
                                                <th style={headerStyle}>Residential</th>
                                                <th style={headerStyle}>Commercial</th>
                                                <th style={headerStyle}>Residential</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {isAddingNew && (
                                                <tr style={rowStyleEven}>
                                                    <td style={cellStyle}>
                                                        <input
                                                            type="text"
                                                            name="area"
                                                            value={newArea.area}
                                                            onChange={handleNewAreaChange}
                                                            style={inputStyle}
                                                            placeholder="Enter area name"
                                                        />
                                                    </td>
                                                    {["buy_com", "buy_res", "rent_com", "rent_res", "lease_com", "lease_res", "owners_com", "owners_res"].map((field) => (
                                                        <td key={field} style={cellStyle}>
                                                            <input
                                                                type="text"
                                                                name={field}
                                                                value={newArea[field]}
                                                                onChange={handleNewAreaChange}
                                                                style={inputStyle}
                                                                placeholder="0"
                                                            />
                                                        </td>
                                                    ))}
                                                    <td style={cellStyle}>
                                                        <div style={{ display: "flex", gap: "5px" }}>
                                                            <Button
                                                                color="success"
                                                                size="sm"
                                                                onClick={handleSubmitNewArea}
                                                                style={{ padding: "0.25rem 0.5rem" }}
                                                            >
                                                                Save
                                                            </Button>
                                                            <Button
                                                                color="danger"
                                                                size="sm"
                                                                onClick={handleCancelAdd}
                                                                style={{ padding: "0.25rem 0.5rem" }}
                                                            >
                                                                Cancel
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                            {paginatedData.length > 0 ? (
                                                paginatedData.map((row, i) => (
                                                    <tr
                                                        key={row.id}
                                                        style={i % 2 === 0 ? rowStyleEven : rowStyleOdd}
                                                    >
                                                        <td style={cellStyle}>{row.area}</td>

                                                        {["buy_com", "buy_res", "rent_com", "rent_res", "lease_com", "lease_res", "owners_com", "owners_res"].map((field) => (
                                                            <td key={field} style={cellStyle}>
                                                                <input
                                                                    type="text"
                                                                    value={row[field] ?? ""}
                                                                    onChange={(e) => {
                                                                        const newValue = e.target.value;
                                                                        if (newValue === "" || /^\d+$/.test(newValue)) {
                                                                            setAreaPropertyCounts(prev =>
                                                                                prev.map(item =>
                                                                                    item.id === row.id ? { ...item, [field]: newValue } : item
                                                                                )
                                                                            );
                                                                        }
                                                                    }}
                                                                    onInput={async (e) => {
                                                                        const updatedValue = e.target.value;
                                                                        try {
                                                                            const updateData = { [field]: updatedValue };
                                                                            const result = await AreaPropertyCountUpdate(row.id, updateData);

                                                                            if (result.success) {
                                                                                const loadingToast = toast.loading("Saving property...");

                                                                                const fieldLabels = {
                                                                                    buy_com: { purpose: "Buy", type: "Commercial" },
                                                                                    buy_res: { purpose: "Buy", type: "Residential" },
                                                                                    rent_com: { purpose: "Rent", type: "Commercial" },
                                                                                    rent_res: { purpose: "Rent", type: "Residential" },
                                                                                    lease_com: { purpose: "Lease", type: "Commercial" },
                                                                                    lease_res: { purpose: "Lease", type: "Residential" },
                                                                                    owners_com: { purpose: "Owners", type: "Commercial" },
                                                                                    owners_res: { purpose: "Owners", type: "Residential" },
                                                                                };

                                                                                const label = fieldLabels[field];
                                                                                const message = `${row.area} — ${label.purpose} ${label.type} updated to ${updatedValue}`;

                                                                                toast.update(loadingToast, {
                                                                                    render: message,
                                                                                    type: "success",
                                                                                    isLoading: false,
                                                                                    autoClose: 3000,
                                                                                    closeOnClick: true,
                                                                                });

                                                                                await fetchPropertyCount();
                                                                            }
                                                                        } catch (error) {
                                                                            toast.error("Error updating property count");
                                                                            console.error(error);
                                                                        }
                                                                    }}
                                                                    style={inputStyle}
                                                                    placeholder="-"
                                                                />
                                                            </td>
                                                        ))}
                                                        <td style={cellStyle}>
                                                            {/* You can add delete/edit buttons here if needed */}
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td
                                                        colSpan="8"
                                                        style={{ ...cellStyle, textAlign: "center", fontStyle: "italic" }}
                                                    >
                                                        No matching areas found.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                <div
                                    style={{
                                        marginTop: "1.5rem",
                                        display: "flex",
                                        justifyContent: "center",
                                        gap: "10px",
                                        alignItems: "center",
                                        color: "white"
                                    }}
                                >
                                    <Button
                                        color="secondary"
                                        onClick={() => goToPage(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        size="sm"
                                    >
                                        Previous
                                    </Button>

                                    <span>
                                        Page {currentPage} of {totalPages}
                                    </span>

                                    <Button
                                        color="secondary"
                                        onClick={() => goToPage(currentPage + 1)}
                                        disabled={currentPage === totalPages || totalPages === 0}
                                        size="sm"
                                    >
                                        Next
                                    </Button>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </AdminGuard>
    );
};

// Reusable styles
const headerStyle = {
    padding: "0.75rem",
    textAlign: "center",
    fontWeight: "600",
    fontSize: "0.9rem",
    border: "1px solid #6b7280",
    color: "white",
    whiteSpace: "nowrap"
};

const cellStyle = {
    padding: "0.75rem",
    textAlign: "center",
    fontWeight: "500",
    color: "white",
    border: "1px solid #4b5563"
};

const inputStyle = {
    cursor: "pointer",
    width: "100%",
    border: "1px solid #4b5563",
    borderRadius: "4px",
    backgroundColor: "#1f2937",
    color: "white",
    padding: "4px 8px",
    fontSize: "0.9rem",
    fontWeight: "500",
    textAlign: "center",
    outline: "none",
};

const rowStyleEven = {
    backgroundColor: "#1f2937"
};

const rowStyleOdd = {
    backgroundColor: "#111827"
};

export default AddSummary;